import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtDate, fmtEUR, getToken, API_BASE, SERVICE_TYPE_LABEL } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { CheckCircle2, FileDown, Trash2, ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import Pagination from "../components/Pagination";
import PageHeader from "../components/PageHeader";

export default function OrderNotes() {
  const [data, setData] = useState({ items: [], page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [statusF, setStatusF] = useState("");
  const [payOpen, setPayOpen] = useState(null);
  const [method, setMethod] = useState("Transferência Bancária");
  const [expanded, setExpanded] = useState({});

  // Add-service dialog
  const [addOpen, setAddOpen] = useState(null); // holds the NE being edited
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedSid, setSelectedSid] = useState("");

  const load = () => api.get("/order-notes", { params: { page, status: statusF } }).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusF]);

  const markPaid = async () => {
    try {
      await api.post(`/order-notes/${payOpen.id}/mark-paid`, { payment_method: method });
      toast.success(`Nota Nº ${payOpen.number} marcada como paga. Serviços renovados +1 ano.`);
      setPayOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const del = async (n) => {
    if (!window.confirm(`Eliminar Nota Nº ${n.number}?`)) return;
    await api.delete(`/order-notes/${n.id}`); toast.success("Eliminada"); load();
  };

  const runCron = async () => {
    try {
      const { data: r } = await api.post("/cron/run");
      toast.success(`${r.created} nota(s) pendentes geradas/atualizadas`); load();
    } catch (e) { toast.error("Erro ao executar cron"); }
  };

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const openAddService = async (n) => {
    setAddOpen(n);
    setSelectedSid("");
    try {
      const { data } = await api.get("/services", { params: { client_id: n.client_id, page_size: 200 } });
      const alreadyIn = new Set((n.items || []).map(it => it.service_id));
      setAvailableServices(data.items.filter(s => !alreadyIn.has(s.id)));
    } catch (e) { toast.error("Erro a carregar serviços"); }
  };

  const confirmAddService = async () => {
    if (!selectedSid) return toast.error("Selecione um serviço");
    try {
      await api.post(`/order-notes/${addOpen.id}/add-service`, { service_id: selectedSid });
      toast.success(`Serviço adicionado à NE Nº ${addOpen.number}`);
      setAddOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const removeItem = async (n, sid) => {
    if (!window.confirm("Remover este serviço da nota?")) return;
    try {
      await api.post(`/order-notes/${n.id}/remove-item`, { service_id: sid });
      toast.success("Serviço removido da nota"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const pdfUrl = (n) => `${API_BASE}/order-notes/${n.id}/pdf?token=${getToken()}`;

  return (
    <div data-testid="notes-page">
      <PageHeader title="Notas de Encomenda" subtitle="Faturação e renovações dos serviços" testid="notes-header"
        actions={<Button onClick={runCron} variant="outline" data-testid="run-cron-btn">Verificar Renovações</Button>} />

      <div className="flex gap-2 mb-4">
        {[{ v: "", l: "Todas" }, { v: "pending", l: "Pendentes" }, { v: "paid", l: "Pagas" }].map(t => (
          <button key={t.v} onClick={() => { setStatusF(t.v); setPage(1); }} data-testid={`note-filter-${t.v || 'all'}`}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition ${statusF === t.v ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="a4w-card overflow-hidden">
        <table className="w-full text-sm" data-testid="notes-table">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-4 py-3 a4w-label">Nº</th>
              <th className="px-4 py-3 a4w-label">Cliente</th>
              <th className="px-4 py-3 a4w-label">Emissão</th>
              <th className="px-4 py-3 a4w-label">Items</th>
              <th className="px-4 py-3 a4w-label">Total</th>
              <th className="px-4 py-3 a4w-label">Estado</th>
              <th className="px-4 py-3 a4w-label">Pago em</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(n => (
              <React.Fragment key={n.id}>
                <tr className="border-t border-slate-100 a4w-table-row">
                  <td className="px-3 py-3">
                    <button onClick={() => toggleExpand(n.id)} className="text-slate-500 hover:text-slate-800" data-testid={`expand-note-${n.number}`}>
                      {expanded[n.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </td>
                  <td className="px-4 py-3 font-bold">#{n.number}</td>
                  <td className="px-4 py-3">{n.client_snapshot?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{fmtDate(n.issue_date)}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{(n.items || []).length}</Badge></td>
                  <td className="px-4 py-3 font-mono font-semibold">{fmtEUR(n.total)}</td>
                  <td className="px-4 py-3">
                    {n.status === "paid" ? <Badge style={{ background: "#DCFCE7", color: "#166534" }}>Pago</Badge>
                      : <Badge style={{ background: "#FFEDD5", color: "#9A3412" }}>Pendente</Badge>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{n.paid_at ? fmtDate(n.paid_at) : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <a href={pdfUrl(n)} target="_blank" rel="noreferrer" data-testid={`pdf-note-${n.number}`}>
                      <Button size="icon" variant="ghost"><FileDown size={16} /></Button>
                    </a>
                    {n.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openAddService(n)} className="ml-1" data-testid={`add-service-${n.number}`}>
                          <Plus size={14} /> Serviço
                        </Button>
                        <Button size="sm" onClick={() => { setPayOpen(n); setMethod("Transferência Bancária"); }} className="bg-green-600 hover:bg-green-700 ml-1" data-testid={`mark-paid-${n.number}`}>
                          <CheckCircle2 size={16} /> Marcar Pago
                        </Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => del(n)} data-testid={`del-note-${n.number}`}><Trash2 size={16} className="text-red-600" /></Button>
                  </td>
                </tr>
                {expanded[n.id] && (
                  <tr className="bg-slate-50">
                    <td></td>
                    <td colSpan="8" className="px-4 py-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Serviços nesta nota</div>
                      <ul className="space-y-2">
                        {(n.items || []).map(it => (
                          <li key={it.service_id} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-slate-200">
                            <div className="flex items-center gap-3 min-w-0">
                              <Badge variant="outline" className="shrink-0">{SERVICE_TYPE_LABEL[it.type] || it.type}</Badge>
                              <span className="truncate"><strong>{it.description}</strong>{it.expires_at && <span className="text-slate-400 ml-2 text-xs">expira {fmtDate(it.expires_at)}</span>}</span>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="font-mono font-semibold">{fmtEUR(it.amount)}</span>
                              {n.status === "pending" && (
                                <button onClick={() => removeItem(n, it.service_id)} className="text-red-600 hover:text-red-800" title="Remover desta nota" data-testid={`remove-item-${n.number}-${it.service_id}`}>
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 text-right text-xs text-slate-500">
                        Subtotal: <span className="font-mono">{fmtEUR(n.subtotal)}</span>
                        {n.iva_rate > 0 && <> · IVA ({n.iva_rate}%): <span className="font-mono">{fmtEUR(n.iva)}</span></>}
                        {" · "}<strong>Total: <span className="font-mono">{fmtEUR(n.total)}</span></strong>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">Sem notas de encomenda.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={data.page} pages={data.pages} total={data.total} onChange={setPage} />

      {/* Mark paid dialog */}
      <Dialog open={!!payOpen} onOpenChange={(o) => !o && setPayOpen(null)}>
        <DialogContent data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Marcar Nota Nº {payOpen?.number} como Paga</DialogTitle>
            <DialogDescription>Todos os serviços anuais associados serão renovados por mais 1 ano. O PDF será atualizado com o estado "Pago".</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Método de Pagamento</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Transferência Bancária">Transferência Bancária</SelectItem>
                <SelectItem value="MB Way">MB Way</SelectItem>
                <SelectItem value="Multibanco">Multibanco</SelectItem>
                <SelectItem value="Numerário">Numerário</SelectItem>
                <SelectItem value="PayPal">PayPal</SelectItem>
                <SelectItem value="Outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(null)}>Cancelar</Button>
            <Button onClick={markPaid} className="bg-green-600 hover:bg-green-700" data-testid="pay-confirm">Confirmar Pagamento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add service dialog */}
      <Dialog open={!!addOpen} onOpenChange={(o) => !o && setAddOpen(null)}>
        <DialogContent data-testid="add-service-dialog">
          <DialogHeader>
            <DialogTitle>Adicionar Serviço à NE Nº {addOpen?.number}</DialogTitle>
            <DialogDescription>Só são listados serviços do cliente <strong>{addOpen?.client_snapshot?.name}</strong> que ainda não estão nesta nota.</DialogDescription>
          </DialogHeader>
          {availableServices.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Não há outros serviços disponíveis para este cliente.</p>
          ) : (
            <div>
              <Label>Serviço</Label>
              <Select value={selectedSid} onValueChange={setSelectedSid}>
                <SelectTrigger data-testid="add-service-select"><SelectValue placeholder="Selecione um serviço" /></SelectTrigger>
                <SelectContent>
                  {availableServices.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      [{SERVICE_TYPE_LABEL[s.type]}] {s.description} — {fmtEUR(s.amount)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(null)}>Cancelar</Button>
            <Button onClick={confirmAddService} disabled={!selectedSid} className="bg-[#1E3A8A] hover:bg-[#172554]" data-testid="add-service-confirm">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
